<script setup lang='ts'>
import { useDebounceFn } from '@vueuse/core';
import { Search } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useFiltersStore } from '@/stores/useFiltersStore';
import { useProductsStore } from '@/stores/useProductsStore';

const { t } = useI18n();
const filtersStore = useFiltersStore();
const { filters } = storeToRefs(filtersStore);

const productsStore = useProductsStore();

const currentSearch = computed(() => filters.value.sorts.find(it => it.type === 'search')?.query ?? '');
const input = ref(currentSearch.value);
let isSearchInProgress = false;

async function performSearch() {
  if (isSearchInProgress)
    return;

  isSearchInProgress = true;
  filtersStore.addSearchingFilter(input.value);

  try {
    await productsStore.fetchProductsList(filters.value);
  }
  finally {
    setTimeout(() => {
      isSearchInProgress = false;
    }, 1500);
  }
}

const handleSearchInput = useDebounceFn(performSearch, 1500);

function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    (event.target as HTMLInputElement).blur();
    performSearch();
  }
}
</script>

<template>
  <div class="search-filter">
    <div class="filter-wrapper" tabindex="0">
      <Search :size="18" color="var(--yb-color-black)" aria-hidden="true" />
      <input
        v-model="input"
        type="search"
        :placeholder="t('search.button')"
        :aria-label="t('search.button')"
        @input="handleSearchInput()"
        @keydown="handleKeyDown"
      >
    </div>
  </div>
</template>

<style lang="css" scoped>
.search-filter {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  .filter-wrapper {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    gap: var(--yb-spacing-xs);
    padding: var(--yb-spacing-sm);
    border: 1px solid var(--yb-color-mediumgray);
    border-radius: var(--yb-radius-sm);
    margin-bottom: var(--yb-spacing-sm);

    input {
      background-color: transparent;
      outline: none;
      border: none;
      color: var(--yb-color-black);
      font-size: var(--yb-text-base);
      caret-color: var(--yb-color-primary);

      &::placeholder {
        color: var(--yb-color-black);
      }
    }
  }
}

/* clears the cross to clear the input from Internet Explorer */
input[type='search']::-ms-clear {
  display: none;
  width: 0;
  height: 0;
}

input[type='search']::-ms-reveal {
  display: none;
  width: 0;
  height: 0;
}

/* clears the cross to clear the input from Chrome */
input[type='search']::-webkit-search-decoration,
input[type='search']::-webkit-search-cancel-button,
input[type='search']::-webkit-search-results-button,
input[type='search']::-webkit-search-results-decoration {
  display: none;
}
</style>
